import React, { useEffect, useRef, useState, useId, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { modalStack, MODAL_TIMINGS } from './modalStack';

export interface KlDialogProps {
    open: boolean;
    onClose: () => void;
    title: string;
    message?: string;
    confirmText?: string;
    cancelText?: string | null;
    onConfirm?: () => void;
    danger?: boolean;
    children?: React.ReactNode;
    id?: string;
}

export const KlDialog: React.FC<KlDialogProps> = ({
    open,
    onClose,
    title,
    message,
    confirmText = 'Aceptar',
    cancelText = 'Cancelar',
    onConfirm,
    danger = false,
    children,
    id: propId
}) => {
    const autoId = useId();
    const dialogId = propId || `kl-dialog-${autoId}`;

    const [isMounted, setIsMounted] = useState(false);
    const [animationState, setAnimationState] = useState<'closed' | 'opening' | 'open' | 'closing'>('closed');
    const [stackDepth, setStackDepth] = useState(0);

    const dialogRef = useRef<HTMLDivElement>(null);
    const prevActiveElementRef = useRef<HTMLElement | null>(null);
    const didUnmountRef = useRef(false);

    const prefersReducedMotion = typeof window !== 'undefined'
        ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
        : false;

    const cleanupAndUnmount = useCallback(() => {
        if (didUnmountRef.current) return;
        didUnmountRef.current = true;
        setIsMounted(false);
        setAnimationState('closed');
    }, []);

    // Registro en la pila modal y gestión de montado
    useEffect(() => {
        if (open) {
            didUnmountRef.current = false;
            prevActiveElementRef.current = document.activeElement as HTMLElement | null;
            setIsMounted(true);

            const unregister = modalStack.push({
                id: dialogId,
                type: 'dialog',
                onClose,
                returnFocusElement: prevActiveElementRef.current
            });

            setStackDepth(modalStack.getDepth(dialogId));

            // Animación de entrada
            setAnimationState('opening');
            const timer = requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    setAnimationState('open');
                });
            });

            return () => {
                cancelAnimationFrame(timer);
                unregister();
            };
        } else if (isMounted) {
            setAnimationState('closing');

            // Respaldo de desmontaje seguro si transitionend no llega en duración + 50ms
            const durationMs = prefersReducedMotion
                ? MODAL_TIMINGS.reducedMotion.fadeMs
                : MODAL_TIMINGS.dialog.fadeMs;

            const safetyTimer = setTimeout(() => {
                cleanupAndUnmount();
            }, durationMs + MODAL_TIMINGS.safetyMarginMs);

            return () => clearTimeout(safetyTimer);
        }
    }, [open, dialogId, onClose, isMounted, prefersReducedMotion, cleanupAndUnmount]);

    // Actualizar profundidad en la pila
    useEffect(() => {
        if (!isMounted) return;
        return modalStack.subscribe(() => {
            setStackDepth(modalStack.getDepth(dialogId));
        });
    }, [isMounted, dialogId]);

    // Foco atrapado y Escape
    useEffect(() => {
        if (animationState !== 'open') return;

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && modalStack.isTop(dialogId)) {
                e.preventDefault();
                onClose();
                return;
            }

            if (e.key === 'Tab' && dialogRef.current) {
                const focusables = dialogRef.current.querySelectorAll<HTMLElement>(
                    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
                );
                if (focusables.length === 0) return;
                const first = focusables[0];
                const last = focusables[focusables.length - 1];

                if (e.shiftKey && document.activeElement === first) {
                    e.preventDefault();
                    last.focus();
                } else if (!e.shiftKey && document.activeElement === last) {
                    e.preventDefault();
                    first.focus();
                }
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [animationState, dialogId, onClose]);

    if (!isMounted) return null;

    const isStacked = stackDepth > 0;

    const dialogStyle: React.CSSProperties = prefersReducedMotion
        ? {
            boxShadow: '0 4px 20px rgba(0,0,0,0.18)',
            transform: 'none',
            opacity: animationState === 'opening' || animationState === 'closing' ? 0 : 1,
            transition: `opacity ${MODAL_TIMINGS.reducedMotion.fadeVar} ease`,
            willChange: animationState === 'opening' || animationState === 'closing' ? 'opacity' : 'auto'
        }
        : {
            boxShadow: '0 4px 20px rgba(0,0,0,0.18)',
            transform: animationState === 'opening' || animationState === 'closing' ? 'scale(0.92)' : 'scale(1)',
            opacity: animationState === 'opening' || animationState === 'closing' ? 0 : 1,
            transition: animationState === 'closing'
                ? `transform ${MODAL_TIMINGS.dialog.fadeVar} ${MODAL_TIMINGS.easeIn}, opacity ${MODAL_TIMINGS.dialog.fadeVar} ${MODAL_TIMINGS.easeIn}`
                : `transform ${MODAL_TIMINGS.dialog.fadeVar} ${MODAL_TIMINGS.easeOut}, opacity ${MODAL_TIMINGS.dialog.fadeVar} ${MODAL_TIMINGS.easeOut}`,
            willChange: animationState === 'opening' || animationState === 'closing' ? 'transform, opacity' : 'auto'
        };

    const zIndex = 115 + stackDepth * 10;

    return createPortal(
        <div
            id={dialogId}
            data-kl-dialog="true"
            data-kl-modal-open={animationState === 'open' ? 'true' : 'false'}
            role="dialog"
            aria-modal="true"
            aria-labelledby={`${dialogId}-title`}
            className="fixed inset-0 flex items-center justify-center p-4 select-none"
            style={{ zIndex }}
        >
            {/* Velo de fondo */}
            <div
                className={`kl-modal-scrim ${animationState === 'open' ? 'is-open' : animationState === 'closing' ? 'is-closing' : ''} ${isStacked ? 'is-stacked' : ''}`}
                onClick={() => {
                    if (modalStack.isTop(dialogId)) {
                        onClose();
                    }
                }}
                aria-hidden="true"
            />

            {/* Contenedor del Diálogo */}
            <div
                ref={dialogRef}
                className="kl-dialog-container relative z-10 w-full max-w-[340px] bg-white dark:bg-[#1a2634] rounded-[14px] p-4 border border-slate-200 dark:border-slate-800 flex flex-col gap-3"
                style={dialogStyle}
                onTransitionEnd={(e) => {
                    if (e.target === dialogRef.current && animationState === 'closing') {
                        cleanupAndUnmount();
                    }
                }}
            >
                <div className="flex items-start gap-3">
                    {danger && (
                        <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 flex items-center justify-center shrink-0">
                            <span className="material-symbols-outlined text-[22px]">warning</span>
                        </div>
                    )}
                    <div className="flex-1 min-w-0">
                        <h3
                            id={`${dialogId}-title`}
                            className="text-[16px] font-bold text-slate-900 dark:text-white m-0"
                        >
                            {title}
                        </h3>
                        {message && (
                            <p className="text-[13px] text-slate-600 dark:text-slate-300 mt-1 leading-snug">
                                {message}
                            </p>
                        )}
                    </div>
                </div>

                {children && <div className="text-[13px] text-slate-700 dark:text-slate-200">{children}</div>}

                {/* Botones de Acción */}
                <div className="flex items-center justify-end gap-2 mt-2 pt-2 border-t border-slate-100 dark:border-slate-800">
                    {Boolean(cancelText) && (
                        <button
                            type="button"
                            data-drawer-close="true"
                            data-kl-modal-close="true"
                            onClick={onClose}
                            className="px-4 py-2 rounded-xl text-slate-600 dark:text-slate-300 font-semibold text-xs hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors active:scale-95"
                        >
                            {cancelText}
                        </button>
                    )}

                    <button
                        type="button"
                        onClick={() => {
                            if (onConfirm) onConfirm();
                            onClose();
                        }}
                        className={`px-4 py-2 rounded-xl text-white font-bold text-xs shadow-md transition-all active:scale-95 ${
                            danger
                                ? 'bg-red-600 hover:bg-red-700 shadow-red-500/20'
                                : 'bg-primary hover:bg-blue-600 shadow-blue-500/20'
                        }`}
                    >
                        {confirmText}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default KlDialog;
